import { useEffect, useRef } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

export function ReviewShell({
  isOpen,
  onClose,
  title,
  subtitle,
  fitContent = false,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  fitContent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />

      <ModalContent h="min(90vh, 50rem)" w={fitContent ? 'fit-content' : undefined}>
        <ModalHeader>
          {title}

          {subtitle == null ? null : (
            <Text fontSize="sm" fontWeight="normal" color="fg.subtle">
              {subtitle}
            </Text>
          )}
        </ModalHeader>

        <ModalCloseButton />

        {children}
      </ModalContent>
    </Modal>
  );
}

export function ErrorAlert({ message }: { message: string }) {
  return (
    <Alert status="error" borderRadius="md">
      <AlertIcon />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

export function PendingBody({ error }: { error: { message: string } | null }) {
  return (
    <ModalBody>
      {error == null ? (
        <Center py={10}>
          <Spinner size="lg" />
        </Center>
      ) : (
        <ErrorAlert message={error.message} />
      )}
    </ModalBody>
  );
}

export function BlockersAlert({
  status,
  messages,
  'data-testid': testId,
}: {
  status: 'error' | 'warning';
  messages: string[];
  'data-testid': string;
}) {
  return (
    <Alert status={status} borderRadius="md" w="auto" py={1.5} mr="auto" data-testid={testId}>
      <AlertIcon />

      <VStack align="stretch" spacing={0}>
        {messages.map((message) => (
          <Text key={message} fontSize="sm">
            {message}
          </Text>
        ))}
      </VStack>
    </Alert>
  );
}

export function GroupHeading({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="sm" fontWeight="semibold" color="fg.muted" textTransform="uppercase">
      {children}
    </Text>
  );
}

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={1}>
      {children}
    </Text>
  );
}

export function OutlineHeading({ children }: { children: React.ReactNode }) {
  return (
    <Box px={2} pb={1}>
      <GroupHeading>{children}</GroupHeading>
    </Box>
  );
}

export function OutlineItem({
  isSelected,
  hasIssue,
  badge,
  count,
  label,
  onSelect,
}: {
  isSelected: boolean;
  hasIssue: boolean;
  badge?: React.ReactNode;
  count?: number;
  label: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Button
      type="button"
      variant="tertiary"
      justifyContent="start"
      py={1.5}
      px={2}
      w="full"
      bg={isSelected ? 'bg.emphasis' : undefined}
      onClick={onSelect}
    >
      <HStack spacing={2} w="100%">
        {badge}
        <Text
          flex={1}
          minW={0}
          noOfLines={1}
          textAlign="left"
          textStyle="sm"
          color="fg.default"
          fontWeight={isSelected ? 'semibold' : 'normal'}
        >
          {label}
        </Text>

        {count == null ? null : (
          <Box py={0.5} px={2} borderRadius="full" bg="bg.surface" flexShrink={0}>
            <Text textStyle="xs" textAlign="center">
              {count}
            </Text>
          </Box>
        )}

        {hasIssue ? <Box w={2} h={2} borderRadius="full" bg="red.500" flexShrink={0} /> : null}
      </HStack>
    </Button>
  );
}

export function ReviewLayout({
  outline,
  paneKey,
  children,
}: {
  outline: React.ReactNode;
  paneKey?: string;
  children: React.ReactNode;
}) {
  const paneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    paneRef.current?.scrollTo({ top: 0 });
  }, [paneKey]);

  return (
    <Flex gap={5} align="stretch" flex={1} minH={0}>
      <VStack
        as="nav"
        align="stretch"
        spacing={1}
        w={pxToRem(280)}
        flexShrink={0}
        position="sticky"
        top={4}
        overflowY="auto"
        bg="bg.canvas"
        borderWidth="1px"
        borderRadius="lg"
        p={2}
      >
        {outline}
      </VStack>
      <Box
        ref={paneRef}
        flex={1}
        minW={0}
        pt={2}
        pr={3}
        overflowY="auto"
        display="flex"
        flexDirection="column"
      >
        {children}
      </Box>
    </Flex>
  );
}
